import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { AlertTriangle, ArrowDown, FlaskConical } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import ResponsiveImage from "@/components/ResponsiveImage";

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.35 + i * 0.13 },
  }),
};

const fadeVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: (d) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: d },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-screen blueprint-grid overflow-hidden flex items-center pt-24 pb-16"
      data-testid="hero-section"
    >
      <motion.span
        style={{ y: bgY }}
        aria-hidden
        className="pointer-events-none select-none absolute -right-10 top-1/2 -translate-y-1/2 font-chivo font-black text-[26vw] leading-none text-outline hidden lg:block"
      >
        H₂O
      </motion.span>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 w-full grid lg:grid-cols-12 gap-12 lg:gap-6 items-center relative">
        <div className="lg:col-span-7">
          <motion.p
            custom={0.25}
            initial="hidden"
            animate="visible"
            variants={fadeVariants}
            className="font-plexmono text-[11px] sm:text-xs tracking-[0.25em] uppercase text-slate-500 flex items-center gap-2 mb-8"
            data-testid="hero-overline"
          >
            <FlaskConical className="w-3.5 h-3.5" />
            Lab2o — Laboratory Research Reagents
          </motion.p>

          <h1 className="font-chivo font-black tracking-tighter text-slate-950 text-5xl sm:text-6xl lg:text-7xl xl:text-[5.4rem] leading-[0.98]" data-testid="hero-headline">
            {["Research-Grade", "Bacteriostatic", "Water for the Lab"].map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  custom={i}
                  initial="hidden"
                  animate="visible"
                  variants={lineVariants}
                  className="block"
                >
                  {i === 1 ? (
                    <span className="text-slate-400">{line}</span>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            custom={0.9}
            initial="hidden"
            animate="visible"
            variants={fadeVariants}
            className="mt-8 max-w-md text-slate-600 text-base sm:text-lg leading-relaxed"
            data-testid="hero-subcopy"
          >
            Sterile, sealed, and supplied in boxes of 10 unlabeled glass
            vials (3 mL or 10 mL) — formulated exclusively as a reagent
            for laboratory research.
          </motion.p>

          <motion.div
            custom={1.05}
            initial="hidden"
            animate="visible"
            variants={fadeVariants}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={() => scrollToId("#products")}
              className="bg-slate-950 text-white px-8 py-4 font-plexmono text-xs tracking-[0.2em] uppercase hover:bg-slate-800 transition-colors flex items-center gap-3"
              data-testid="hero-shop-now-button"
            >
              Shop Now
              <ArrowDown className="w-3.5 h-3.5" />
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={() => scrollToId("#order")}
              className="border border-slate-300 px-8 py-4 font-plexmono text-xs tracking-[0.2em] uppercase text-slate-700 hover:border-slate-950 hover:text-slate-950 transition-colors"
              data-testid="hero-how-to-order-button"
            >
              How to Order
            </motion.button>
          </motion.div>

          <motion.div
            custom={1.2}
            initial="hidden"
            animate="visible"
            variants={fadeVariants}
            className="mt-10 max-w-lg border-l-2 border-red-600 bg-red-50/60 px-5 py-4 flex items-start gap-3"
            data-testid="hero-disclaimer"
          >
            <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
            <p className="text-red-700 text-sm font-medium leading-snug">
              For research purposes only. Not for human or veterinary use or
              consumption.
            </p>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative min-w-0 w-full max-w-full">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.55 }}
          >
            <motion.div style={{ y: imgY }} className="relative">
              <div className="border border-slate-200 bg-white p-3">
                <div className="overflow-hidden bg-slate-50">
                  <ResponsiveImage
                    src="/images/hero-vials.png"
                    alt="Lab2o sterile glass research vials, 3 mL and 10 mL bacteriostatic water"
                    width={1408}
                    height={768}
                    className="w-full max-w-full aspect-[4/5] object-cover animate-float-slow"
                    loading="eager"
                    fetchPriority="high"
                    dataTestid="hero-vial-image"
                  />
                </div>
                <div className="flex items-center justify-between pt-3 px-1">
                  <span className="font-plexmono text-[10px] tracking-[0.2em] uppercase text-slate-400">
                    Fig. 01 — Sterile vials
                  </span>
                  <span className="font-plexmono text-[10px] tracking-[0.2em] uppercase text-slate-400">
                    3 mL / 10 mL
                  </span>
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4 bg-slate-950 text-white px-4 py-2 font-plexmono text-[10px] tracking-[0.2em] uppercase">
                Research Reagent
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
